import { useState } from "react";
import { ChevronDown, Layers } from "lucide-react";
import { Column } from "../../components/DataTable";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import type {
  AssetCoverageApplicationItem,
  AssetCoverageAssetRow,
  AssetCoverageDrilldownRow,
  AssetCoverageScoreRow,
  AssetCoverageToolDetail,
} from "./services";
import { DEVICE_TYPE_COLORS } from "../cmdb/cmdbUtils";
import { ApplicationsCell } from "../cmdb/components/device/ApplicationsCell";
import { formatDeviceType } from "../cmdb/components/device/deviceUtils";
import { getApplicationMappings } from "./services";

// Same type badge as Configuration > CMDB > Devices.
export const renderTypeBadge = (row: { type?: string }) => {
  const type = row.type?.trim();
  if (!type) return "-";
  return (
    <span
      className={`inline-flex w-[110px] items-center justify-center whitespace-nowrap px-2 py-0.5 rounded-md text-xs ${DEVICE_TYPE_COLORS[type.toUpperCase()] || "bg-muted text-muted-foreground"}`}
    >
      {formatDeviceType(type)}
    </span>
  );
};

// Same Applications cell as Configuration > CMDB > Devices.
export function renderApplicationsCell(mappings: AssetCoverageApplicationItem[]) {
  const apps = mappings.map((m) => m.applicationName);
  if (apps.length === 0) return <span className="text-muted-foreground">-</span>;
  if (apps.length === 1) {
    return (
      <div className="w-full max-w-full overflow-hidden flex items-center">
        <span
          className="inline-flex items-center max-w-full truncate px-2 py-0.5 rounded-md text-xs font-medium bg-muted/70 text-foreground border border-border/60"
          title={apps[0]}
        >
          {apps[0]}
        </span>
      </div>
    );
  }
  return <ApplicationsCell apps={apps} searchPlaceholder="Search" />;
}

// Same badge shape as the Type column; green when onboarded, red otherwise.
const toolStatusBadgeClass = (isOnboarded: boolean) =>
  `inline-flex w-[130px] items-center justify-center whitespace-nowrap px-2 py-0.5 rounded-md text-xs font-medium ${
    isOnboarded
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
      : "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
  }`;

export const ASSET_COVERAGE_TOOL_DETAIL_COLUMNS: Column<AssetCoverageToolDetail>[] = [
  {
    key: "tool",
    header: "Tool",
    align: "center",
    headerAlign: "center",
    render: (row) => row.tool || row.toolName || "-",
  },
  {
    key: "onboardingStatus",
    header: "Onboarding Status",
    align: "center",
    headerAlign: "center",
    render: (row) => {
      const isOnboarded =
        row.onboardingStatus === "Onboarded" ||
        row.onboarded === true ||
        row.status === "ONBOARDED";
      const statusLabel = isOnboarded ? "Onboarded" : "Not Onboarded";
      return (
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
            isOnboarded
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400"
          }`}
        >
          <span
            aria-hidden
            className={`size-1.5 rounded-full ${
              isOnboarded ? "bg-emerald-500" : "bg-red-500"
            }`}
          />
          {statusLabel}
        </span>
      );
    },
  },
  {
    key: "lastOnline",
    header: "Last Sync",
    align: "center",
    headerAlign: "center",
    render: (row) => (
      <span className="tabular-nums text-muted-foreground">
        {row.lastOnline ?? row.lastSync ?? "-"}
      </span>
    ),
  },
];

export function ApplicationMappingCell({ mappings }: { mappings: AssetCoverageApplicationItem[] }) {
  const [open, setOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");

  if (!mappings || mappings.length === 0) {
    return <span className="text-xs font-normal text-muted-foreground">-</span>;
  }

  if (mappings.length === 1) {
    const m = mappings[0];
    return (
      <div className="flex flex-col min-w-0 items-start text-left">
        <span className="text-xs font-normal text-foreground truncate max-w-full" title={m.applicationName}>
          {m.applicationName}
        </span>
        {m.organization && (
          <span className="text-[10px] font-normal text-muted-foreground truncate max-w-full" title={m.organization}>
            {m.organization}
          </span>
        )}
      </div>
    );
  }

  const filtered = filterQuery.trim()
    ? mappings.filter(
        (m) =>
          m.applicationName.toLowerCase().includes(filterQuery.toLowerCase()) ||
          (m.organization && m.organization.toLowerCase().includes(filterQuery.toLowerCase())),
      )
    : mappings;

  return (
    <div className="flex flex-col min-w-0 items-start text-left">
      <span className="text-xs font-normal text-foreground truncate max-w-full" title={mappings[0].applicationName}>
        {mappings[0].applicationName}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer mt-0.5"
          >
            <span>+{mappings.length - 1} more</span>
            <ChevronDown className="size-3 opacity-70" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-80 p-3 shadow-lg border border-border bg-popover z-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-border">
            <div className="flex items-center gap-1.5">
              <Layers className="size-3.5 text-muted-foreground" />
              <h4 className="text-xs font-semibold text-foreground">
                Applications ({mappings.length})
              </h4>
            </div>
          </div>

          {mappings.length > 4 && (
            <div className="mb-2">
              <input
                type="text"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                placeholder="Search"
                className="w-full text-xs px-2.5 py-1.5 rounded-md border border-border bg-muted/30 text-foreground outline-none focus:border-blue-500/60"
              />
            </div>
          )}

          <div className="max-h-56 overflow-y-auto space-y-2 pr-1 divide-y divide-border/40">
            {filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">No matching applications</p>
            ) : (
              filtered.map((m, idx) => (
                <div key={m.applicationId || idx} className={idx > 0 ? "pt-2" : ""}>
                  <p className="text-xs font-normal text-foreground">{m.applicationName}</p>
                  {m.organization && (
                    <p className="text-[10px] text-muted-foreground">Org: {m.organization}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Columns for the Onboarded / Not Onboarded drilldown drawer table. */
export const ASSET_COVERAGE_DRILLDOWN_COLUMNS: Column<AssetCoverageDrilldownRow>[] = [
  {
    key: "name",
    header: "Asset Name",
    align: "left",
    headerAlign: "center",
    sortable: true,
    width: "240px",
    minWidth: 220,
    render: (row) => <span className="text-xs font-normal text-foreground">{row.assetName || "-"}</span>,
  },
  { key: "type", header: "Type", align: "center", sortable: true, width: "160px", minWidth: 150, render: renderTypeBadge },
  {
    key: "ip",
    header: "IP Address",
    align: "center",
    sortable: true,
    width: "240px",
    minWidth: 220,
    render: (row) => <span className="text-xs font-normal text-foreground tabular-nums">{row.ip || "-"}</span>,
  },
  { key: "environment", header: "Environment", align: "center", sortable: true, width: "130px", minWidth: 120 },
  {
    key: "applications",
    header: "Applications",
    sortable: true,
    align: "left",
    headerAlign: "center",
    width: "240px",
    minWidth: 230,
    render: (row) => renderApplicationsCell(getApplicationMappings({ applications: row.applications })),
  },
];

const SCORE_ROW_KNOWN_KEYS = new Set([
  "id", "assetName", "type", "ip", "environment", "applications", "assetScore", "severity", "tools",
]);

function getScoreRowToolMap(row: AssetCoverageScoreRow): Record<string, string> {
  if (row.tools && typeof row.tools === "object") return row.tools;
  const flat: Record<string, string> = {};
  Object.entries(row).forEach(([k, v]) => {
    if (!SCORE_ROW_KNOWN_KEYS.has(k) && typeof v === "string" && /onboarded/i.test(v)) flat[k] = v;
  });
  return flat;
}

/** Columns for the KPI drilldowns; tool columns come from the response rows. Asset Score is shown for the score drilldown only. */
export function getAssetCoverageScoreColumns(
  rows: AssetCoverageScoreRow[],
  includeScore = true,
): Column<AssetCoverageScoreRow>[] {
  const toolKeys: string[] = [];
  rows.forEach((r) =>
    Object.keys(getScoreRowToolMap(r)).forEach((k) => {
      if (!toolKeys.includes(k)) toolKeys.push(k);
    }),
  );

  const toolColumns: Column<AssetCoverageScoreRow>[] = toolKeys.map((toolKey) => ({
    key: `tool_${toolKey}`,
    header: toolKey,
    align: "center",
    headerAlign: "center",
    width: "200px",
    render: (row) => {
      const value = getScoreRowToolMap(row)[toolKey];
      if (!value) return <span className="text-xs text-muted-foreground">-</span>;
      const isOnboarded = value.trim().toLowerCase() === "onboarded";
      return (
        <span className={toolStatusBadgeClass(isOnboarded)}>{value}</span>
      );
    },
  }));

  return [
    ...(ASSET_COVERAGE_DRILLDOWN_COLUMNS.slice(0, 4).map((c) =>
      c.key === "name" ? { ...c, width: "240px" } : c.key === "ip" ? { ...c, width: "240px" } : c,
    ) as unknown as Column<AssetCoverageScoreRow>[]),
    ASSET_COVERAGE_DRILLDOWN_COLUMNS[4] as unknown as Column<AssetCoverageScoreRow>,
    ...(includeScore
      ? [
          {
            key: "assetScore",
            header: "Asset Score",
            sortable: true,
            align: "center",
            headerAlign: "center",
            width: "130px",
            render: (row: AssetCoverageScoreRow) => (
              <span className="tabular-nums text-foreground">
                {typeof row.assetScore === "number" ? row.assetScore : "-"}
              </span>
            ),
          } as Column<AssetCoverageScoreRow>,
        ]
      : []),
    ...toolColumns,
  ];
}

/** Dynamic tool columns use `tool_<toolName>` keys; the suffix is sent as `sortTool`. */
export const ASSET_TOOL_SORT_PREFIX = "tool_";

function getToolStatusMap(row: AssetCoverageAssetRow): Record<string, string> {
  return row.tools ?? {};
}

/** Columns for the asset roster table; sorting is server-side. Tool columns are built from the response rows. */
export function getAssetCoverageAssetColumns(
  rows: AssetCoverageAssetRow[],
  onOpenTools: (asset: AssetCoverageAssetRow, tool: string) => void,
): Column<AssetCoverageAssetRow>[] {
  const toolKeys: string[] = [];
  rows.forEach((r) =>
    Object.keys(getToolStatusMap(r)).forEach((k) => {
      if (!toolKeys.includes(k)) toolKeys.push(k);
    }),
  );

  const toolColumns: Column<AssetCoverageAssetRow>[] = toolKeys.map((toolKey) => ({
    key: `${ASSET_TOOL_SORT_PREFIX}${toolKey}`,
    header: toolKey,
    sortable: true,
    align: "center",
    headerAlign: "center",
    width: "200px",
    render: (row) => {
      const value = getToolStatusMap(row)[toolKey];
      if (!value) return <span className="text-xs text-muted-foreground">-</span>;
      const isOnboarded = value.trim().toLowerCase() === "onboarded";
      return (
        <button
          type="button"
          onClick={() => onOpenTools(row, toolKey)}
          title="View tool details"
          className={`cursor-pointer hover:opacity-80 ${toolStatusBadgeClass(isOnboarded)}`}
        >
          {value}
        </button>
      );
    },
  }));

  return [
    {
      key: "name",
      header: "Asset Name",
      align: "left",
      headerAlign: "center",
      sortable: true,
      width: "240px",
      minWidth: 220,
      render: (row) => <span className="text-xs font-normal text-foreground">{row.name || row.assetName || "-"}</span>,
    },
    { key: "type", header: "Type", align: "center", sortable: true, width: "160px", minWidth: 150, render: renderTypeBadge },
    {
      key: "ip",
      header: "IP Address",
      align: "center",
      sortable: true,
      width: "240px",
      minWidth: 220,
      render: (row) => <span className="text-xs font-normal text-foreground tabular-nums">{row.ip || "-"}</span>,
    },
    { key: "environment", header: "Environment", align: "center", sortable: true, width: "130px", minWidth: 120 },
    {
      key: "applications",
      header: "Applications",
      sortable: true,
      align: "left",
      headerAlign: "center",
      width: "240px",
      minWidth: 230,
      render: (row) => renderApplicationsCell(getApplicationMappings(row)),
    },
    ...toolColumns,
  ];
}
